// src/locales/en/setup.js
export const setup = {
  // ========== WIZARD GENERAL ==========
  // Main wizard titles and navigation
  welcomeTitle: "Welcome to Your Business Setup",
  welcomeSubtitle: "Let's get your business up and running in just a few steps",
  loadingSetup: "Loading Setup Wizard",
  loadingMessage: "Please wait while we prepare your setup experience",
  setupCompleted: "Setup completed successfully!",
  completionError: "Failed to complete setup. Please try again.",
  
  // Navigation
  previous: "Previous",
  next: "Next",
  skip: "Skip",
  finish: "Complete Setup",
  stepOf: "Step {{current}} of {{total}}",
  stepCompleted: "{{step}} completed successfully",
  stepError: "Failed to complete step. Please try again.",

  // Step titles for progress indicator
  emailVerification: "Email Verification",
  businessProfile: "Business Profile",
  shopCreation: "Shop Setup",
  warehouseCreation: "Warehouse Setup",
  courierAssignment: "Courier Services",
  planSelection: "Plan Selection",
  kycDocuments: "KYC Documents",
  dropshippingJoin: "Join Provider",
  complete: "Complete",

  // Loading states
  loadingProfile: "Loading Profile",
  loadingProfileMessage: "Fetching your business information...",
  loadingPlans: "Loading Plans",
  loadingPlansDesc: "Fetching available subscription plans...",
  loadingCouriers: "Loading Courier Services",
  loadingCouriersDesc: "Fetching available courier companies...",
  loadError: "Failed to load setup data",

  // ========== EMAIL VERIFICATION ==========
  verifyEmailTitle: "Verify Your Email Address",
  verifyEmailDescription: "We've sent a verification code to {{email}}. Please enter it below.",
  verificationCode: "Verification Code",
  verificationCodeHint: "Enter the 6-digit code from your email",
  enterVerificationCode: "Please enter the verification code",
  verifying: "Verifying...",
  verifyEmail: "Verify Email",
  verificationError: "Invalid verification code. Please try again.",
  emailVerifiedSuccess: "Email verified successfully!",
  
  // Resend functionality
  resendCode: "Resend Code",
  resending: "Resending...",
  resendIn: "Resend in {{seconds}}s",
  verificationCodeSent: "Verification code sent to your email",
  resendError: "Failed to send verification code",
  
  // Already verified state
  emailAlreadyVerified: "Email Already Verified",
  emailVerifiedMessage: "Your email has been successfully verified",
  
  // Change email (in verification step)
  changeEmail: "Change Email Address",
  changeEmailDescription: "Entered the wrong email? Change it here and we'll send a new code.",
  newEmailLabel: "New Email Address",
  newEmailPlaceholder: "Enter your new email address",
  sendNewCode: "Update & Send Code",
  emailChanged: "Email updated! A new verification code has been sent.",
  changingEmail: "Updating...",
  emailChangeError: "Failed to update email address",

  // Help section
  needHelp: "Need Help?",
  checkSpamFolder: "Check your spam/junk folder",
  checkEmailAddress: "Ensure the email address is correct",
  contactSupportIf: "Contact support if you don't receive the code",

  // ========== BUSINESS PROFILE ==========
  businessProfileTitle: "Complete Your Business Profile",
  businessProfileDescription: "Add your business details to personalize your experience",
  
  // Form fields
  businessName: "Business Name",
  businessNamePlaceholder: "Ex: XYZ Company",
  businessNameRequired: "Business name is required",
  businessNameMinLength: "Business name must be at least 2 characters",
  
  legalName: "Legal Business Name",
  legalNamePlaceholder: "Ex: XYZ Company (Pvt) Ltd",
  legalNameRequired: "Legal business name is required", 
  legalNameMinLength: "Legal name must be at least 2 characters",
  legalNameHint: "Official registered name for legal documents",
  
  businessAddress: "Business Address",
  businessAddressPlaceholder: "Enter your business address",
  
  businessPhone: "Business Phone",
  businessPhonePlaceholder: "Enter business phone number",
  invalidPhone: "Please enter a valid phone number",
  
  businessWebsite: "Website",
  invalidWebsite: "Please enter a valid website URL",
  
  businessDescription: "Business Description",
  businessDescriptionPlaceholder: "Describe your business...",
  businessDescriptionHint: "Brief description of your business and what you sell",
  
  // Logo upload
  businessLogo: "Business Logo",
  uploadLogo: "Click to upload logo",
  logoFormats: "PNG, JPG, JPEG, WEBP up to 2MB",
  logoSizeHint: "Recommended size: 200x200 pixels, maximum 2MB",
  invalidLogoFormat: "Invalid logo format. Use PNG, JPG, JPEG, or WEBP",
  logoTooLarge: "Logo file is too large. Maximum size is 2MB",
  
  // Actions
  updating: "Updating...",
  saveBusinessProfile: "Save Business Profile",
  businessProfileUpdated: "Business profile updated successfully",
  businessUpdateError: "Failed to update business profile",

  // ========== SHOP CREATION ==========
  createNewShop: "Create Your First Shop",
  setupYourShop: "Set up your shop to start selling products and managing orders",
  
  // Shop details
  shopName: "Shop Name",
  shopNamePlaceholder: "Enter your shop name",
  nameRequired: "Shop name is required",
  nameMinLength: "Shop name must be at least 2 characters",
  
  shopType: "Shop Type",
  
  // Shop types (matching CreateShopPage translations)
  online: "Online Store",
  onlineDescription: "Sell products online with delivery options",
  pos: "POS Store", 
  posDescription: "Physical store with point-of-sale system",
  dropshipper: "Dropshipper",
  dropshipperDescription: "Sell products with supplier fulfillment",
  
  // Order prefix
  orderPrefix: "Order Number Prefix",
  prefixRequired: "Order prefix is required",
  prefixMinLength: "Prefix must be at least 2 characters",
  prefixTaken: "This prefix is already taken",
  prefixAvailable: "Prefix is available",
  prefixHint: "This prefix will be used for order numbers (e.g., ABC-001)",
  
  // Contact details
  address: "Shop Address", 
  addressPlaceholder: "Enter shop address",
  phone: "Shop Phone",
  phonePlaceholder: "Enter shop phone number",
  
  // Delivery charges
  deliveryCharges: "Delivery Charges",
  deliveryChargesRequired: "All delivery charges must be filled",
  deliveryChargesHint: "Set delivery charges for each shipping zone. Use 0 for free delivery",
  bulkEdit: "Bulk Edit",
  setFreeDeliveryAll: "Free Delivery All",
  bulkEditDeliveryCharges: "Bulk Edit Delivery Charges",
  deliveryChargeAmount: "Delivery Charge Amount",
  bulkEditHint: "This amount will be applied to all shipping zones",
  applyToAll: "Apply to All",
  bulkEditApplied: "Bulk edit applied to all zones",
  freeDeliveryApplied: "Free delivery applied to all zones",
  invalidBulkEditValue: "Please enter a valid amount",
  
  // Actions and states
  creating: "Creating...",
  createShop: "Create Shop",
  shopCreatedSuccess: "Shop \"{{shopName}}\" created successfully",
  failedToCreate: "Failed to create shop",
  failedToLoadZones: "Failed to load shipping zones",
  loadingData: "Loading shop data...",
  loadingDataDesc: "Please wait while we prepare the shop creation form",

  // ========== WAREHOUSE CREATION ==========
  createNewWarehouse: "Create Your Warehouse", 
  setupYourWarehouse: "Set up your warehouse details and information",
  
  warehouseName: "Warehouse Name",
  warehouseNamePlaceholder: "Enter warehouse name (e.g., Main Warehouse, Storage Unit A)",
  warehouseNameRequired: "Warehouse name is required",
  warehouseNameMinLength: "Warehouse name must be at least 2 characters",
  
  addressPlaceholder: "Enter complete warehouse address...",
  addressHint: "Include street address, city, and postal code for better identification",
  
  phoneHint: "Contact number for warehouse operations and coordination",
  
  createWarehouse: "Create Warehouse",
  warehouseCreatedSuccess: "Warehouse \"{{warehouseName}}\" created successfully!",

  // ========== COURIER ASSIGNMENT ==========
  courierAssignmentTitle: "Select Courier Services",
  courierAssignmentDescription: "Choose courier companies to handle your deliveries",
  
  // Selection controls
  selectedCouriers: "Selected: {{selected}} of {{total}}",
  selectAll: "Select All",
  deselectAll: "Deselect All",
  selectAtLeastOneCourier: "Please select at least one courier service",
  
  // Courier features
  apiSupported: "API Supported",
  webhookSupported: "Webhook Supported", 
  autoWaybill: "Auto Waybill",
  
  // Actions
  skipForNow: "Skip for Now",
  assigning: "Assigning...",
  assignCouriers: "Assign Couriers",
  couriersAssignedSuccess: "{{count}} courier services assigned successfully",
  courierAssignmentError: "Failed to assign courier services",
  
  // Empty state
  noCouriersAvailable: "No Couriers Available",
  noCouriersDescription: "No courier companies are available at the moment. You can add them later.",
  couriersLoadError: "Failed to load courier services",
  
  // Info section
  courierInfoTitle: "About Courier Assignment",
  courierInfoPoint1: "You can assign multiple courier services",
  courierInfoPoint2: "Couriers help automate shipping and tracking",
  courierInfoPoint3: "You can manage courier settings later",
  courierInfoPoint4: "This step can be skipped and configured later",

  // ========== DROPSHIPPING KYC STEP ==========
  dropshippingKycTitle: "Upload KYC Documents",
  dropshippingKycDescription: "Upload your identity and business documents to complete reseller verification.",
  uploadingKyc: "Uploading...",
  submitKyc: "Submit Documents",
  kycSubmittedSuccess: "KYC documents submitted successfully",
  kycSubmitError: "Failed to submit KYC documents",

  // ========== DROPSHIPPING JOIN REQUEST STEP ==========
  dropshippingJoinTitle: "Complete Your Reseller Application",
  dropshippingJoinDescription: "Fill in the details below to join {{providerName}}'s dropshipping network.",
  joiningProvider: "Submitting...",
  submitJoinRequest: "Join Now",
  joinRequestSuccess: "Successfully joined {{providerName}}!",
  joinRequestError: "Failed to submit join request",
  loadingProviderInfo: "Loading provider information...",

  // ========== PLAN SELECTION ==========
  planSelectionTitle: "Choose Your Plan",
  planSelectionDescription: "Select the plan that best fits your business needs",

  // Trial banner
  trialOffer: "30-Day Free Trial Available!",
  trialOfferDescription: "Try any paid plan free for 30 days. No payment required now.",
  
  // Plan features
  mostPopular: "Most Popular",
  selected: "Selected",
  month: "month",
  free: "Free",
  
  // Trial badges  
  free60DayTrial: "Free for 60 days",
  freeForever: "Free Forever",
  
  // Plan benefits
  noPaymentRequired: "No Payment Required",
  upgradeDowngradeAnytime: "Upgrade/Downgrade Anytime",
  
  // Plan summary
  yourChoice: "Your Choice",
  free60DaysNow: "FREE for 60 days now!",
  billingStartsAfterTrial: "Billing starts only after your trial ends",
  
  // Actions
  activating: "Activating...",
  activateFreePlan: "Activate Free Plan", 
  startTrialNow: "Start Trial Now",
  selectPlan: "Please select a plan",
  freePlanActivatedSuccess: "{{planName}} activated successfully!",
  trialActivatedSuccess: "{{trialDays}}-day trial started for {{planName}}!",
  planActivationError: "Failed to activate plan",
  plansLoadError: "Failed to load subscription plans",
  
  // Information sections
  whyNoPaymentNeeded: "Why No Payment Needed?",
  freeTrialExplanation: "60 days is enough time to fully explore all features",
  flexibilityExplanation: "Change plans anytime - upgrade or downgrade as needed", 
  testFeaturesExplanation: "Test all premium features without any commitments",
  
  helpCompare: "Compare plan features to find the best fit",
  helpUpgrade: "Upgrade or downgrade plans anytime", 
  helpBilling: "Billing starts only after your trial period ends",

  // ========== SETUP COMPLETE ==========
  setupCompleteTitle: "🎉 Setup Complete!",
  setupCompleteMessage: "Congratulations! {{businessName}} is now ready to start selling.",
  setupSummaryTitle: "What We've Set Up",
  
  // Completed items
  emailVerificationComplete: "✅ Email address verified",
  businessProfileComplete: "✅ Business profile created", 
  shopCreatedComplete: "✅ Shop \"{{shopName}}\" created",
  warehouseCreatedComplete: "✅ Warehouse \"{{warehouseName}}\" created",
  couriersAssignedComplete: "✅ {{count}} courier services assigned",
  couriersSkippedComplete: "⏭️ Courier setup skipped (can configure later)",
  planSelectedComplete: "✅ {{planName}} plan activated",
  
  // Next steps
  nextStepsTitle: "What's Next?",
  nextStep1Title: "Add Your Products",
  nextStep1Description: "Start adding products to your inventory and set up your catalog",
  nextStep2Title: "Configure Settings", 
  nextStep2Description: "Customize your shop settings, payment methods, and shipping options",
  nextStep3Title: "Start Selling",
  nextStep3Description: "Your store is ready! Begin receiving orders and managing your business",
  
  // Reseller flow completion
  resellerSetupCompleteMessage: "You are now connected to {{providerName}} as a reseller!",
  resellerConnectedComplete: "✅ Connected to {{providerName}}",
  resellerNextStep1Title: "Browse Products",
  resellerNextStep1Description: "Go to Dropshipping Products to start assigning products to your shop",
  resellerNextStep2Title: "Set Your Prices",
  resellerNextStep2Description: "Customize selling prices for each product you assign",
  resellerNextStep3Title: "Start Selling",
  resellerNextStep3Description: "Your dropshipper shop is ready to receive orders",

  // Welcome bonus
  welcomeBonusTitle: "Welcome Bonus",
  welcomeBonusDescription: "Enjoy your free 30-day trial period and explore all the features we have to offer!",
  
  // Actions
  finishing: "Finishing...",
  getStarted: "Get Started",
  
  // Support
  needHelpTitle: "Need Help Getting Started?", 
  supportDescription: "We're here to help you succeed with your business",
  viewDocumentation: "View Documentation",
  contactSupport: "Contact Support", 
  watchTutorials: "Watch Tutorials",

  // ========== COMMON FORM ELEMENTS ==========
  // Common validation messages
  required: "This field is required",
  minLength: "Must be at least {{count}} characters",
  maxLength: "Must not exceed {{count}} characters",
  invalidEmail: "Please enter a valid email address",
  invalidUrl: "Please enter a valid URL",
  invalidNumber: "Please enter a valid number",
  
  // Common actions
  save: "Save",
  cancel: "Cancel", 
  delete: "Delete",
  edit: "Edit",
  update: "Update",
  create: "Create",
  loading: "Loading...",
  saving: "Saving...",
  deleting: "Deleting...",
  
  // File upload
  uploadFile: "Upload File",
  chooseFile: "Choose File",
  fileSelected: "File selected",
  noFileSelected: "No file selected",
  fileTooLarge: "File is too large",
  invalidFileType: "Invalid file type",
  
  // Form hints
  optional: "Optional",
  recommended: "Recommended",
  required_asterisk: "* Required fields"
};